// src/pages/CompanyManagement/tabs/SettingsTab.jsx
import React from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  Grid,
  Divider,
  Alert
} from '@mui/material';
import {
  Videocam as VideocamIcon,
  Notifications as NotificationsIcon
} from '@mui/icons-material';

const SettingsTab = ({ company, onChange, t }) => {
  const settings = company.settings || {};
  const retention = settings.cameraFootageRetention || {
    defaultRetentionDays: 30,
    maxRetentionDays: 90,
    minRetentionDays: 1,
    autoDeleteAfterRetention: false
  };
  const notifications = settings.notifications || {
    emailNotifications: true,
    notifyOnNewRequest: true,
    notifyOnApproval: true,
    notifyOnRejection: true
  };

  const handleRetentionChange = (field, value) => {
    const numValue = parseInt(value, 10);
    if (isNaN(numValue) || numValue < 1 || numValue > 365) return;

    onChange({
      settings: {
        ...settings,
        cameraFootageRetention: {
          ...retention,
          [field]: numValue
        }
      }
    });
  };

  const handleAutoDeleteToggle = (checked) => {
    onChange({
      settings: {
        ...settings,
        cameraFootageRetention: {
          ...retention,
          autoDeleteAfterRetention: checked
        }
      }
    });
  };

  const handleNotificationChange = (field, checked) => {
    onChange({
      settings: {
        ...settings,
        notifications: {
          ...notifications,
          [field]: checked
        }
      }
    });
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('CompanyManagement.settings_title')}
      </Typography>
      <Divider sx={{ mb: 3 }} />

      {/* Camera Footage Requests Toggle */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          avatar={<VideocamIcon />}
          title={t('CompanyManagement.camera_footage_requests')}
          subheader={t('CompanyManagement.camera_footage_requests_subtitle')}
        />
        <Divider />
        <CardContent>
          <FormControlLabel
            control={
              <Switch
                checked={settings.cameraFootageRequestsEnabled || false}
                onChange={(e) => onChange({
                  settings: {
                    ...settings,
                    cameraFootageRequestsEnabled: e.target.checked
                  }
                })}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body2">{t('CompanyManagement.enable_camera_footage_requests')}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('CompanyManagement.enable_camera_footage_requests_help')}
                </Typography>
              </Box>
            }
          />
          {!settings.cameraFootageRequestsEnabled && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {t('CompanyManagement.camera_footage_requests_disabled_warning')}
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Camera Footage Retention */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <VideocamIcon color="primary" />
            <Typography variant="h6">
              {t('CompanyManagement.camera_footage_retention')}
            </Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />

          <Alert severity="info" sx={{ mb: 3 }}>
            {t('CompanyManagement.camera_footage_retention_info')}
          </Alert>

          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label={t('CompanyManagement.minimum_retention_days')}
                value={retention.minRetentionDays}
                onChange={(e) => handleRetentionChange('minRetentionDays', e.target.value)}
                helperText={t('CompanyManagement.minimum_retention_days_helper')}
                inputProps={{ min: 1, max: 365 }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label={t('CompanyManagement.default_retention_days')}
                value={retention.defaultRetentionDays}
                onChange={(e) => handleRetentionChange('defaultRetentionDays', e.target.value)}
                helperText={t('CompanyManagement.default_retention_days_helper')}
                inputProps={{ min: 1, max: 365 }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label={t('CompanyManagement.maximum_retention_days')}
                value={retention.maxRetentionDays}
                onChange={(e) => handleRetentionChange('maxRetentionDays', e.target.value)}
                helperText={t('CompanyManagement.maximum_retention_days_helper')}
                inputProps={{ min: 1, max: 365 }}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={retention.autoDeleteAfterRetention}
                    onChange={(e) => handleAutoDeleteToggle(e.target.checked)}
                    color="primary"
                  />
                }
                label={t('CompanyManagement.auto_delete_footage')}
              />
            </Grid>
          </Grid>

          {retention.minRetentionDays > retention.defaultRetentionDays && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {t('CompanyManagement.validation_min_greater_than_default')}
            </Alert>
          )}

          {retention.defaultRetentionDays > retention.maxRetentionDays && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {t('CompanyManagement.validation_default_greater_than_max')}
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Notification Settings */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <NotificationsIcon color="primary" />
            <Typography variant="h6">
              {t('CompanyManagement.notification_preferences')}
            </Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />

          <Alert severity="info" sx={{ mb: 3 }}>
            {t('CompanyManagement.notification_preferences_info')}
          </Alert>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={notifications.emailNotifications}
                    onChange={(e) => handleNotificationChange('emailNotifications', e.target.checked)}
                    color="primary"
                  />
                }
                label={t('CompanyManagement.enable_email_notifications')}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={notifications.notifyOnNewRequest}
                    onChange={(e) => handleNotificationChange('notifyOnNewRequest', e.target.checked)}
                    color="primary"
                    disabled={!notifications.emailNotifications}
                  />
                }
                label={t('CompanyManagement.notify_on_new_request')}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={notifications.notifyOnApproval}
                    onChange={(e) => handleNotificationChange('notifyOnApproval', e.target.checked)}
                    color="primary"
                    disabled={!notifications.emailNotifications}
                  />
                }
                label={t('CompanyManagement.notify_on_approval')}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={notifications.notifyOnRejection}
                    onChange={(e) => handleNotificationChange('notifyOnRejection', e.target.checked)}
                    color="primary"
                    disabled={!notifications.emailNotifications}
                  />
                }
                label={t('CompanyManagement.notify_on_rejection')}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SettingsTab;
